import {
  createContext,
  useContext,
  useState,
  type JSX,
  type PropsWithChildren,
} from "react";
import { createStore, type StoreApi } from "zustand/vanilla";
import { MathUtils, Matrix4, Vector3 } from "three";
import { SurfaceSelection } from "./surface-selection-pass";
import type { SceneEvents } from "@/components/common/scene-events-context";
import { surfaceDimensions } from "./depth-surface";
import {
  isAnalyticSelection,
  isPipeRunSelection,
  wandSelectionShape,
  type WandSelection,
  type WandShape,
} from "./wand-selection";

export type SurfaceRequest = {
  shape?: WandShape;
  addTo?: SurfaceSelection;
  cloudId: string;
  cameraId: string;
  point: Vector3;
  inverseViewProjection: Matrix4;
};

export type SurfaceWandState = {
  shape?: WandShape;
  generation: number;
  addNextSeed: boolean;
  isolateSelection: boolean;
  otherPointsOpacity: number;
  request: SurfaceRequest | undefined;
  selection: WandSelection | undefined;
  retainedSelections: WandSelection[];
  error: string | undefined;
};

const Context = createContext<StoreApi<SurfaceWandState> | undefined>(
  undefined,
);

/** @returns A canvas-scoped transient selection owner. */
export function SurfaceWandProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const [store] = useState(() =>
    createStore<SurfaceWandState>(() => ({
      shape: "plane",
      generation: 0,
      addNextSeed: false,
      isolateSelection: false,
      otherPointsOpacity: 1,
      request: undefined,
      selection: undefined,
      retainedSelections: [],
      error: undefined,
    })),
  );
  return <Context.Provider value={store}>{children}</Context.Provider>;
}

/** @returns The optional wand store for the current canvas. */
export function useSurfaceWandStore(): StoreApi<SurfaceWandState> | undefined {
  return useContext(Context);
}

/**
 * Cancel pending work and remove the current selection.
 *
 * @param store Canvas-local selection owner.
 */
export function clearSurfaceWand(store: StoreApi<SurfaceWandState>): void {
  store.setState((state) => ({
    generation: state.generation + 1,
    addNextSeed: false,
    isolateSelection: false,
    otherPointsOpacity: 1,
    request: undefined,
    selection: undefined,
    retainedSelections: [],
    error: undefined,
  }));
}

/** @param store Canvas-local selection owner. */
export function closeSurfaceWand(store: StoreApi<SurfaceWandState>): void {
  store.setState((state) => ({
    generation: state.generation + 1,
    addNextSeed: false,
    request: undefined,
    selection:
      state.selection?.coverage === "growing"
        ? { ...state.selection, coverage: "partial" }
        : state.selection,
    error: undefined,
  }));
}

/**
 * @param store Canvas-local selection owner.
 * @param selected Plane to remove without disturbing other selections.
 */
export function removeSurfaceWandSelection(
  store: StoreApi<SurfaceWandState>,
  selected: WandSelection,
): void {
  const state = store.getState();
  if (state.selection === selected) {
    store.setState({
      generation: state.generation + 1,
      selection: undefined,
      request: undefined,
      addNextSeed: false,
      isolateSelection: false,
      error: undefined,
    });
  } else {
    store.setState({
      retainedSelections: state.retainedSelections.filter(
        (surface) => surface !== selected,
      ),
    });
  }
}

function retainSelection(selection: WandSelection): WandSelection {
  if (isPipeRunSelection(selection)) {
    return { ...selection, coverage: "partial" };
  }
  return {
    ...selection,
    id: selection.id ?? MathUtils.generateUUID(),
    coverage: selection.coverage === "growing" ? "partial" : selection.coverage,
  };
}

/**
 * @param store Canvas-local selection owner.
 * @param shape Optional shape for subsequent selections.
 */
export function newSurfaceWandSelection(
  store: StoreApi<SurfaceWandState>,
  shape?: WandShape,
): void {
  store.setState((state) => ({
    shape: shape ?? state.shape ?? "plane",
    generation: state.generation + 1,
    addNextSeed: false,
    isolateSelection: false,
    otherPointsOpacity: 1,
    request: undefined,
    selection: undefined,
    retainedSelections: state.selection
      ? [...state.retainedSelections, retainSelection(state.selection)]
      : state.retainedSelections,
    error: undefined,
  }));
}

/**
 * @param store Canvas-local selection owner.
 * @param selected Retained polygon to make active.
 */
export function activateSurfaceWandSelection(
  store: StoreApi<SurfaceWandState>,
  selected: WandSelection,
): void {
  store.setState((state) => ({
    shape: wandSelectionShape(selected),
    generation: state.generation + 1,
    addNextSeed: false,
    isolateSelection: false,
    request: undefined,
    selection: selected,
    retainedSelections: [
      ...state.retainedSelections.filter((surface) => surface !== selected),
      ...(state.selection && state.selection !== selected
        ? [retainSelection(state.selection)]
        : []),
    ],
    error: undefined,
  }));
}

/**
 * @param store Canvas-local selection owner.
 * @param request The next clicked seed and its captured camera pose.
 */
export function requestSurfaceWand(
  store: StoreApi<SurfaceWandState>,
  request: Omit<SurfaceRequest, "addTo">,
): void {
  const selected = store.getState().selection;
  const activeShape = selected ? wandSelectionShape(selected) : "plane";
  store.setState((state) => ({
    generation: state.generation + 1,
    addNextSeed: false,
    isolateSelection: false,
    error: undefined,
    request: {
      ...request,
      shape:
        state.addNextSeed && state.selection
          ? activeShape
          : (state.shape ?? "plane"),
      addTo:
        state.addNextSeed &&
        state.selection &&
        isAnalyticSelection(state.selection)
          ? state.selection
          : undefined,
    },
  }));
}

// Selections are replaced, never mutated, so each object's area can be cached.
const surfaceAreas = new WeakMap<WandSelection, number>();

function surfaceArea(surface: WandSelection): number {
  if (isPipeRunSelection(surface)) return 0;
  let area = surfaceAreas.get(surface);
  if (area === undefined) {
    area =
      surfaceDimensions(
        surface.positions,
        new Vector3(surface.plane.x, surface.plane.y, surface.plane.z),
        new Vector3(0, 0, 1),
        surface.worldSpace ? surface : undefined,
      )?.area ?? 0;
    surfaceAreas.set(surface, area);
  }
  return area;
}

/**
 * @param store Canvas-local selection owner.
 * @param events Toolbar-to-canvas commands and status notifications.
 * @returns Detaches the toolbar and resets its availability.
 */
export function connectSurfaceWandToolbar(
  store: StoreApi<SurfaceWandState>,
  events: Pick<SceneEvents, "surfaceWandAddSeed" | "surfaceWandAddSeedState">,
): () => void {
  function publish(): void {
    const {
      selection,
      request,
      addNextSeed,
      retainedSelections,
      otherPointsOpacity,
    } = store.getState();
    const count = retainedSelections.length + (selection ? 1 : 0);
    const surfaces = selection
      ? [...retainedSelections, selection]
      : retainedSelections;
    const totalArea = surfaces.reduce(
      (sum, surface) => sum + surfaceArea(surface),
      0,
    );
    events.surfaceWandAddSeedState.emit({
      shape: store.getState().shape ?? "plane",
      canReport: count > 0 && !request && surfaces.every(isAnalyticSelection),
      hasPipeRuns: surfaces.some(isPipeRunSelection),
      canStop:
        request?.shape === "pipe-run" ||
        (!!selection &&
          isPipeRunSelection(selection) &&
          selection.coverage === "growing"),
      enabled: !!selection && isAnalyticSelection(selection) && !request,
      armed: addNextSeed,
      canClear: count > 0 || !!request,
      count,
      otherPointsOpacity,
      totalArea,
    });
  }
  const command = events.surfaceWandAddSeed.on((action) => {
    const state = store.getState();
    if (typeof action === "object") {
      if ("shape" in action) {
        if (action.shape !== (state.shape ?? "plane")) {
          newSurfaceWandSelection(store, action.shape);
        }
      } else if (Number.isFinite(action.otherPointsOpacity)) {
        store.setState({
          otherPointsOpacity: MathUtils.clamp(action.otherPointsOpacity, 0, 1),
          isolateSelection: false,
        });
      }
    } else {
      switch (action) {
        case "new": {
          newSurfaceWandSelection(store);

          break;
        }
        case "clear": {
          clearSurfaceWand(store);

          break;
        }
        case "stop": {
          closeSurfaceWand(store);

          break;
        }
        default:
          if (
            action === "toggle" &&
            state.selection &&
            isAnalyticSelection(state.selection) &&
            !state.request
          ) {
            store.setState({
              addNextSeed: !state.addNextSeed,
              isolateSelection: false,
              otherPointsOpacity: 1,
            });
          } else {
            publish();
          }
      }
    }
  });
  const unsubscribe = store.subscribe(publish);
  publish();
  return () => {
    command.dispose();
    unsubscribe();
    events.surfaceWandAddSeedState.emit({
      enabled: false,
      armed: false,
      canClear: false,
      count: 0,
      totalArea: 0,
      otherPointsOpacity: 1,
    });
  };
}
